import { useEffect, useState, useRef } from "react";
import { Play, X, Film } from "lucide-react";
import { supabase } from "../services/supabaseClient";

interface ConceptVideo {
  id: number;
  video_url: string;
  duration_sec: number;
  video_style: "explainer" | "motion";
  script_json: {
    hook?: string;
    recap?: string;
  };
}

interface Props {
  conceptId: number;
  sourceTable?: "concepts" | "concepts_plus1";
}

const STYLE_LABEL: Record<ConceptVideo["video_style"], string> = {
  explainer: "Watch Explainer",
  motion: "Watch Animated",
};

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

function VideoCard({ video }: { video: ConceptVideo }) {
  const [open, setOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [speed, setSpeed] = useState(1);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (open) {
      videoRef.current?.play().catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = speed;
  }, [speed]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        videoRef.current?.pause();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const mins = Math.floor((video.duration_sec ?? 90) / 60);
  const secs = (video.duration_sec ?? 90) % 60;
  const durationLabel = `${mins}:${String(secs).padStart(2, "0")}`;

  function close() {
    setOpen(false);
    videoRef.current?.pause();
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full relative overflow-hidden rounded-2xl group text-left h-40"
        aria-label={`${STYLE_LABEL[video.video_style]} — ${durationLabel}`}
      >
        {/* Cinematic dark gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#07090f] via-[#0c1525] to-[#150920]" />

        {/* Ambient saffron glow */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-64 h-64 rounded-full bg-coral-warm/[0.07] blur-3xl group-hover:bg-coral-warm/[0.14] transition-all duration-700" />
        </div>

        {/* Top-left badge */}
        <div className="absolute top-3.5 left-4 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.08] border border-white/[0.12]">
          <Film className="w-3 h-3 text-coral-warm" />
          <span className="text-[10px] font-bold text-white/80 uppercase tracking-widest">AI Animated</span>
        </div>

        {/* Top-right duration */}
        <div className="absolute top-3.5 right-4 px-2 py-1 rounded-full bg-black/50 border border-white/[0.08]">
          <span className="text-xs text-white/50 font-mono">{durationLabel}</span>
        </div>

        {/* Centred play button with pulse ring */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative flex items-center justify-center">
            <span className="absolute w-20 h-20 rounded-full bg-coral-warm/20 motion-safe:animate-ping" />
            <div className="relative z-10 w-14 h-14 rounded-full bg-gradient-to-br from-coral-warm to-orange-500 flex items-center justify-center shadow-xl shadow-coral-warm/30 group-hover:scale-110 group-hover:shadow-coral-warm/50 transition-all duration-300">
              <Play className="w-6 h-6 text-white fill-white ml-0.5" />
            </div>
          </div>
        </div>

        {/* Bottom scrim + label */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent px-4 pt-10 pb-3.5">
          {video.script_json?.hook && (
            <p className="text-[11px] text-white/40 line-clamp-1 mb-0.5">{video.script_json.hook}</p>
          )}
          <p className="text-sm font-bold text-white">{STYLE_LABEL[video.video_style]}</p>
        </div>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-sm"
          onClick={close}
        >
          <div
            className="w-full max-w-3xl bg-ocean-deep rounded-2xl overflow-hidden shadow-2xl max-h-[90dvh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 border-b border-white/10 flex-shrink-0">
              <span className="text-sm font-semibold text-white truncate mr-3">
                {STYLE_LABEL[video.video_style]}
              </span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-white/40 hidden sm:inline">Speed</span>
                <div className="flex items-center gap-0.5 overflow-x-auto">
                  {SPEEDS.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSpeed(s)}
                      className={`text-xs px-2 py-1.5 rounded transition-colors min-w-[36px] ${
                        speed === s
                          ? "bg-coral-warm text-black font-semibold"
                          : "text-white/50 hover:text-white active:text-white"
                      }`}
                    >
                      {s}x
                    </button>
                  ))}
                </div>
                <button
                  onClick={close}
                  className="p-2 rounded-full hover:bg-white/10 active:bg-white/20 transition-colors ml-1"
                  aria-label="Close"
                >
                  <X className="w-4 h-4 text-white/60" />
                </button>
              </div>
            </div>

            {/* Video */}
            <div className="bg-black aspect-video flex items-center justify-center min-h-0">
              {loadError ? (
                <p className="text-sm text-white/60 px-6 text-center">
                  Couldn't load this video. Check your connection and try again.
                </p>
              ) : (
                <video
                  ref={videoRef}
                  src={video.video_url}
                  controls
                  playsInline
                  preload="metadata"
                  onError={() => setLoadError(true)}
                  className="w-full h-full"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function ConceptVideoPlayer({ conceptId, sourceTable = "concepts" }: Props) {
  const [video, setVideo] = useState<ConceptVideo | null>(null);

  useEffect(() => {
    supabase
      .from("concept_videos")
      .select("id, video_url, duration_sec, video_style, script_json")
      .eq("concept_id", conceptId)
      .eq("source_table", sourceTable)
      .eq("status", "done")
      .then(({ data }) => {
        if (!data?.length) return;
        const all = data as ConceptVideo[];
        const motion = all.find(v => v.video_style === "motion");
        setVideo(motion ?? all[0]);
      });
  }, [conceptId, sourceTable]);

  if (!video) return null;

  return (
    <div className="mb-4">
      <VideoCard video={video} />
    </div>
  );
}
